import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";
import { invalidateSettings } from "@/hooks/useSettings";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOCALE_LABELS = { fr: "Français", nl: "Nederlands", en: "English" };

export default function SettingsAdmin() {
    const [data, setData] = useState(null);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        try {
            const { data: d } = await axios.get(`${API}/admin/settings`, { withCredentials: true });
            setData(d);
        } catch {
            toast.error("Chargement impossible.");
        }
    };

    useEffect(() => { load(); }, []);

    if (!data) {
        return (
            <div className="flex items-center gap-2 text-[#64748B]">
                <Loader2 size={16} className="animate-spin" /> Chargement…
            </div>
        );
    }

    const setContact = (k, v) => setData({ ...data, contact: { ...data.contact, [k]: v } });
    const setLang = (l, v) => setData({ ...data, languages: { ...data.languages, [l]: v } });
    const setSocial = (k, v) => setData({ ...data, socials: { ...data.socials, [k]: v } });

    const save = async () => {
        setSaving(true);
        try {
            await axios.put(
                `${API}/admin/settings`,
                { contact: data.contact, languages: data.languages, socials: data.socials },
                { withCredentials: true },
            );
            invalidateSettings();
            toast.success("Paramètres enregistrés.");
        } catch {
            toast.error("Enregistrement impossible.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <section data-testid="settings-admin" className="space-y-10 max-w-3xl">
            <div>
                <div className="overline text-[#64748B]">Paramètres du site</div>
                <div className="font-serif text-2xl md:text-3xl">Coordonnées, langues & réseaux</div>
                <p className="text-sm text-[#64748B] mt-2">
                    Ces informations sont propagées automatiquement dans le header, le footer, la page de contact
                    et les balises SEO.
                </p>
            </div>

            {/* Contact */}
            <div className="border border-slate-200 bg-white p-8">
                <h3 className="font-serif text-xl mb-6">Coordonnées</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
                    <div className="md:col-span-2">
                        <label className="overline text-[#64748B]">Adresse</label>
                        <input className="field" value={data.contact.address || ""} onChange={(e) => setContact("address", e.target.value)} data-testid="settings-address" />
                    </div>
                    <div>
                        <label className="overline text-[#64748B]">Téléphone</label>
                        <input className="field" value={data.contact.phone || ""} onChange={(e) => setContact("phone", e.target.value)} data-testid="settings-phone" />
                    </div>
                    <div>
                        <label className="overline text-[#64748B]">Email</label>
                        <input type="email" className="field" value={data.contact.email || ""} onChange={(e) => setContact("email", e.target.value)} data-testid="settings-email" />
                    </div>
                    <div className="md:col-span-2">
                        <label className="overline text-[#64748B]">Horaires</label>
                        <input className="field" value={data.contact.hours || ""} onChange={(e) => setContact("hours", e.target.value)} data-testid="settings-hours" />
                    </div>
                </div>
            </div>

            {/* Languages */}
            <div className="border border-slate-200 bg-white p-8">
                <h3 className="font-serif text-xl mb-2">Langues du site</h3>
                <p className="text-sm text-[#64748B] mb-6">
                    Le français est toujours activé (langue par défaut, URLs sans préfixe). Activez les autres langues
                    pour qu'elles apparaissent dans le sélecteur de langue et pour émettre les balises hreflang associées.
                </p>
                <div className="space-y-3">
                    {["fr", "nl", "en"].map((l) => (
                        <label key={l} className="flex items-center gap-3">
                            <input
                                type="checkbox"
                                checked={!!data.languages[l]}
                                disabled={l === "fr"}
                                onChange={(e) => setLang(l, e.target.checked)}
                                data-testid={`settings-lang-${l}`}
                            />
                            <span className="text-sm">
                                {LOCALE_LABELS[l]}{l === "fr" && <span className="text-[#64748B]"> — toujours activé</span>}
                            </span>
                        </label>
                    ))}
                </div>
            </div>

            {/* Socials */}
            <div className="border border-slate-200 bg-white p-8">
                <h3 className="font-serif text-xl mb-6">Réseaux sociaux</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
                    <div>
                        <label className="overline text-[#64748B]">Instagram</label>
                        <input className="field" value={data.socials.instagram || ""} onChange={(e) => setSocial("instagram", e.target.value)} data-testid="settings-instagram" />
                    </div>
                    <div>
                        <label className="overline text-[#64748B]">Facebook</label>
                        <input className="field" value={data.socials.facebook || ""} onChange={(e) => setSocial("facebook", e.target.value)} data-testid="settings-facebook" />
                    </div>
                    <div>
                        <label className="overline text-[#64748B]">LinkedIn</label>
                        <input className="field" value={data.socials.linkedin || ""} onChange={(e) => setSocial("linkedin", e.target.value)} data-testid="settings-linkedin" />
                    </div>
                </div>
            </div>

            <div className="sticky bottom-4 flex justify-end">
                <button onClick={save} disabled={saving} data-testid="settings-save" className="text-sm px-6 py-3 bg-[#1E3A5F] text-white hover:bg-[#152a45] transition-colors inline-flex items-center gap-2 shadow-lg disabled:opacity-60">
                    {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} strokeWidth={1.5} />}
                    Enregistrer les paramètres
                </button>
            </div>
        </section>
    );
}
